import mBaseModel from '@/models/mBaseModel';
import UtilsData from '@/utilities/UtilsData';
import formatter from '@/utilities/Formatter';
import UtilsObject from '@/utilities/UtilsObject';

import { ModelWithSmartProperties } from '@/models/ModelWithSmartProperties';
import { ModelFormatters } from '@/models/ModelFormatters';
import { ModelPropertyInfos } from '@/models/ModelPropertyInfos';

export default class mDealAcisLiability extends ModelWithSmartProperties {
  static Defaults = () => {
    return {
      BondName: null,
      OfferedPct: 100.0,
      Balance: NaN,
      Premium: 0.0,
      CallMultiplier: 1.0,
    };
  };

  static vocDbToLabel(dbName = null) {
    const ats = Object.freeze({
      OfferedPct: 'Offered Pct',
      BondName: 'Bond Name',
      Balance: 'Balance',
      Premium: 'Premium',
      CallMultiplier: 'Call Multiplier',
    });
    if (dbName) {
      return UtilsObject.getByPath(ats, dbName);
    }
    return ats;
  }

  constructor(rawModelFields = {}) {
    super(rawModelFields);
    this.ats = {
      ...mDealAcisLiability.Defaults(),
    };

    this.defineModelProperty('BondName', ModelPropertyInfos.Text);

    this.defineModelProperty('OfferedPct', ModelPropertyInfos.DecimalNumber);
    this.defineModelProperty('Balance', ModelPropertyInfos.DecimalNumber2Digits);
    this.defineModelProperty('Premium', ModelPropertyInfos.DecimalNumber);
    this.defineModelProperty('CallMultiplier', ModelPropertyInfos.DecimalNumber);

    this.setRawFields({
      ...this.ats,
      ...rawModelFields
    });
  }

  getRawFieldsFormattedForBackEnd() {
    return {
      BondName: this.BondName$,
      OfferedPct: this.OfferedPct$,
      Balance: this.Balance$,
      Premium: this.Premium$,
      CallMultiplier: this.CallMultiplier$
    };
  }

  validate() {
    const createErrorDescription = (msg, fieldName, fieldSysName) => {
      return {
        modelName: 'ACIS',
        msg,
        field: fieldName,
        fieldSysName
      };
    };

    this._localValidationStatus = true;
    this._localValidationResults = [];

    // 'Offered Percent' is a percentage, where 0 <= Percent <= 100… default is 100%.
    if (!(this.OfferedPct$ >= 0 && this.OfferedPct$ <= 100)) {
      this._localValidationResults.push(createErrorDescription(
        '0% <= Offered % <= 100%',
        mDealAcisLiability.vocDbToLabel('OfferedPct'),
        'OfferedPct'
      ));
    }

    // 'ACIS Premium' is a numeric, where 0 <= Premium < 100. There is no default.
    if (!(this.Premium$ >= 0 && this.Premium$ < 100)) {
      this._localValidationResults.push(createErrorDescription(
        '0 <= Premium < 100',
        mDealAcisLiability.vocDbToLabel('Premium'),
        'Premium'
      ));
    }

    // 'Call Multiplier' is a numeric, where 0 <= Multiplier < 50... default is 1.0.
    if (!(this.CallMultiplier$ >= 0 && this.CallMultiplier$ < 50)) {
      this._localValidationResults.push(createErrorDescription(
        '0 <= CallMultiplier < 50',
        mDealAcisLiability.vocDbToLabel('CallMultiplier'),
        'CallMultiplier'
      ));
    }

    for (let [fieldSysName, fValue] of Object.entries(this.ats)) {
      const fieldName = mDealAcisLiability.vocDbToLabel(fieldSysName)
      const error = createErrorDescription('', fieldName, fieldSysName);
      switch (fieldSysName) {
        default:
          error.msg = 'Field is required.';
          if (typeof fValue === 'number') {
            if (isNaN(fValue) || fValue == null) {
              this._localValidationResults.push(error);
            }
          } else {
            if (UtilsData.isEmpty(fValue)) {
              this._localValidationResults.push(error);
            } else if (fValue === 'Invalid date') {
              error.msg = 'Invalid Date.';
              this._localValidationResults.push(error);
            }
          }
      }
    }
    this._localValidationStatus = this._localValidationResults.length === 0;

    return this._localValidationResults;
  }
}
